/* Zeus dimensions toggle.
 *
 * Adds a button over the product gallery that swaps the main image to a
 * dimensioned drawing and back. Driven by custom.dimension_media, the 1-based
 * media position of that drawing.
 *
 * WHICH drawing depends on the add-ons ticked. custom.dimension_media_addon is
 * index-aligned with custom.add_ons; a non-zero entry means that add-on has its
 * own drawing. Tick the add-on bay on a storage shelf and the button measures
 * the combined unit instead of the base - otherwise the customer is shown a
 * width that no longer matches the configuration they just built.
 *
 * It works by clicking Dawn's own thumbnail for the target position rather than
 * reaching into <media-gallery> internals. Dawn's thumbnail handler already
 * does everything correctly - sets the active slide, moves the slider, updates
 * aria state - so nothing has to be reimplemented and nothing breaks when Dawn
 * changes.
 */
(() => {
  const el = document.getElementById('zeus-dimensions-data');
  if (!el) return;
  let data;
  try {
    data = JSON.parse(el.textContent);
  } catch {
    return;
  }
  const BASE = parseInt(data?.position, 10);
  if (!Number.isFinite(BASE) || BASE < 1) return;
  const SHOW = String(data?.show || 'Show dimensions').trim();
  const HIDE = String(data?.hide || 'Hide dimensions').trim();

  // Index-aligned with the add-on tiles; 0 or missing means "no own drawing".
  const overrides = (Array.isArray(data?.addon) ? data.addon : []).map((n) => parseInt(n, 10) || 0);

  const thumbFor = (pos) =>
    document.querySelector('.thumbnail-list__item[data-media-position="' + pos + '"]');

  const activePos = () => {
    const items = [...document.querySelectorAll('.product__media-item')];
    const i = items.findIndex((x) => x.classList.contains('is-active'));
    return i >= 0 ? i + 1 : 1;
  };

  // The last ticked add-on that has a drawing wins, so the most recent choice
  // is the one measured.
  const targetPos = () => {
    const checks = [...document.querySelectorAll('[data-addon-check]')];
    for (let i = checks.length - 1; i >= 0; i--) {
      if (checks[i].checked && overrides[i]) return overrides[i];
    }
    return BASE;
  };

  const start = () => {
    const gallery = document.querySelector('media-gallery');
    if (!gallery || !thumbFor(BASE)) return false;

    // Every drawing out of the rail, but still in the DOM so it stays clickable.
    for (const pos of [BASE, ...overrides.filter(Boolean)]) {
      const t = thumbFor(pos);
      if (t) t.classList.add('zeus-dim__thumb');
    }

    const wrap = gallery.querySelector('.product__media-list')?.parentElement || gallery;
    if (wrap.querySelector('.zeus-dim__btn')) return true;

    let showing = false;
    let previous = activePos();

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'zeus-dim__btn';
    btn.setAttribute('aria-pressed', 'false');
    btn.innerHTML =
      '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
      '<path d="M3 7h18M3 17h18M6 4v6M18 4v6M6 14v6M18 14v6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>' +
      '</svg><span>' + SHOW + '</span>';

    const label = (s) => {
      btn.querySelector('span').textContent = s ? HIDE : SHOW;
      btn.setAttribute('aria-pressed', s ? 'true' : 'false');
      btn.classList.toggle('is-on', s);
    };

    const goTo = (pos) => {
      const b = thumbFor(pos)?.querySelector('button');
      if (b) b.click();
    };

    btn.addEventListener('click', () => {
      if (!showing) previous = activePos();
      goTo(showing ? previous : targetPos());
      showing = !showing;
      label(showing);
    });

    // Changing an add-on while the drawing is open re-measures immediately,
    // rather than leaving a width on screen that no longer matches the cart.
    const reassert = () => setTimeout(() => {
      if (showing) goTo(targetPos());
    }, 120);

    document.addEventListener('change', (e) => {
      if (!e.target.closest('[data-addon-check]')) return;
      reassert();
    });

    // An add-on quantity can change the picture too (one pair of pegs vs two),
    // and zeus-add-ons repaints the gallery when it does. Without this the
    // drawing would be swapped out from under a button still reading "Hide
    // dimensions". The stepper fires no change event, so both paths are needed.
    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-addon-qty-step]')) return;
      reassert();
    });
    document.addEventListener('input', (e) => {
      if (!e.target.closest('[data-addon-qty]')) return;
      reassert();
    });

    // If the customer browses the gallery themselves, the button is stale.
    gallery.addEventListener('click', (e) => {
      if (e.target.closest('.zeus-dim__btn')) return;
      setTimeout(() => {
        const drawings = [BASE, ...overrides.filter(Boolean)];
        const on = drawings.includes(activePos());
        if (on !== showing) {
          showing = on;
          label(showing);
        }
      }, 120);
    });

    wrap.classList.add('zeus-dim__wrap');
    wrap.appendChild(btn);
    return true;
  };

  if (!start()) {
    // Dawn renders the gallery late on some templates.
    let tries = 0;
    const t = setInterval(() => {
      if (start() || ++tries > 20) clearInterval(t);
    }, 250);
  }
})();
